import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  formatApiError,
  twoFaSetup,
  twoFaActivate,
  twoFaVerify,
  changePasswordRequest,
} from "../utils/auth";
import { ShieldCheck, Loader2, Eye, EyeOff, KeyRound, Smartphone } from "lucide-react";

const BRAND = "#5b3df5";

const inputClass =
  "mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-[13px] text-slate-900 placeholder:text-slate-400 transition-colors focus:border-[#5b3df5] focus:outline-none focus:ring-2 focus:ring-[#5b3df5]/20";

const buttonClass =
  "inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#5b3df5] px-4 py-3 text-[13px] font-semibold text-white shadow-sm shadow-[#5b3df5]/25 transition-colors hover:bg-[#4a2fe0] focus:outline-none focus:ring-2 focus:ring-[#5b3df5] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

function passwordChecks(pwd) {
  return [
    { label: "Minimal 12 karakter", ok: pwd.length >= 12 },
    { label: "Huruf besar (A-Z)", ok: /[A-Z]/.test(pwd) },
    { label: "Huruf kecil (a-z)", ok: /[a-z]/.test(pwd) },
    { label: "Angka (0-9)", ok: /[0-9]/.test(pwd) },
  ];
}

// Panel dekoratif kanan — murni hiasan, tidak menyimpan atau memproses apa pun.
function MosaicPanel() {
  return (
    <svg
      viewBox="0 0 600 800"
      preserveAspectRatio="xMidYMid slice"
      className="h-full w-full"
      aria-hidden="true"
    >
      {/* Baris 1 */}
      <rect x="0" y="0" width="200" height="200" fill="#7c3aed" />
      <g fill="none" stroke="#a78bfa" strokeWidth="13">
        <path d="M56,0 A144,144 0 0 0 200,144" />
        <path d="M96,0 A104,104 0 0 0 200,104" />
        <path d="M136,0 A64,64 0 0 0 200,64" />
      </g>
      <circle cx="46" cy="154" r="17" fill="#a78bfa" />

      <rect x="200" y="0" width="200" height="200" fill="#1e1b4b" />
      <polygon points="248,40 262,26 276,40 262,54" fill="#f97316" />
      <polygon points="262,40 276,26 290,40 276,54" fill="#facc15" />
      <polygon points="290,40 304,26 318,40 304,54" fill="#f97316" />
      <polygon points="304,40 318,26 332,40 318,54" fill="#facc15" />
      <rect x="228" y="86" width="144" height="7" fill="#22d3ee" />
      <g fill="#22d3ee">
        <rect x="228" y="102" width="3" height="26" /><rect x="236" y="102" width="3" height="26" />
        <rect x="244" y="102" width="3" height="26" /><rect x="252" y="102" width="3" height="26" />
        <rect x="260" y="102" width="3" height="26" /><rect x="268" y="102" width="3" height="26" />
        <rect x="276" y="102" width="3" height="26" /><rect x="284" y="102" width="3" height="26" />
        <rect x="292" y="102" width="3" height="26" /><rect x="300" y="102" width="3" height="26" />
        <rect x="308" y="102" width="3" height="26" /><rect x="316" y="102" width="3" height="26" />
        <rect x="324" y="102" width="3" height="26" /><rect x="332" y="102" width="3" height="26" />
        <rect x="340" y="102" width="3" height="26" /><rect x="348" y="102" width="3" height="26" />
        <rect x="356" y="102" width="3" height="26" /><rect x="364" y="102" width="3" height="26" />
      </g>
      <rect x="228" y="150" width="86" height="7" fill="#4f46e5" />

      <rect x="400" y="0" width="200" height="200" fill="#4338ca" />
      <g fill="#312e81">
        <circle cx="440" cy="24" r="3" /><circle cx="456" cy="24" r="3" /><circle cx="472" cy="24" r="3" /><circle cx="488" cy="24" r="3" />
        <circle cx="440" cy="40" r="3" /><circle cx="456" cy="40" r="3" /><circle cx="472" cy="40" r="3" /><circle cx="488" cy="40" r="3" />
        <circle cx="440" cy="56" r="3" /><circle cx="456" cy="56" r="3" /><circle cx="472" cy="56" r="3" /><circle cx="488" cy="56" r="3" />
      </g>
      <polygon points="540,18 580,42 580,88 540,112 500,88 500,42" fill="#3730a3" />
      <polygon points="540,18 580,42 540,66 500,42" fill="#4f46e5" />
      <g stroke="#3730a3" strokeWidth="7">
        <line x1="404" y1="196" x2="470" y2="130" /><line x1="424" y1="196" x2="490" y2="130" />
        <line x1="444" y1="196" x2="510" y2="130" /><line x1="464" y1="196" x2="530" y2="130" />
        <line x1="484" y1="196" x2="550" y2="130" /><line x1="504" y1="196" x2="570" y2="130" />
        <line x1="524" y1="196" x2="590" y2="130" />
      </g>

      {/* Baris 2 */}
      <rect x="0" y="200" width="200" height="200" fill="#4f46e5" />
      <polygon points="70,236 104,286 36,286" fill="#60a5fa" />
      <polygon points="70,300 96,338 44,338" fill="#3b82f6" />

      <rect x="200" y="200" width="200" height="200" fill="#312e81" />
      <path d="M300,300 C266,300 244,278 244,244 C278,244 300,266 300,300 Z" fill="#4f46e5" />
      <path d="M300,300 C334,300 356,278 356,244 C322,244 300,266 300,300 Z" fill="#4f46e5" />
      <rect x="296" y="300" width="8" height="42" fill="#4f46e5" />
      <path d="M300,352 L308,372 L328,380 L308,388 L300,408 L292,388 L272,380 L292,372 Z" fill="#ffffff" />

      <rect x="400" y="200" width="200" height="200" fill="#4f46e5" />
      <g fill="#312e81">
        <rect x="432" y="236" width="10" height="56" /><rect x="450" y="252" width="10" height="40" />
        <rect x="468" y="228" width="10" height="64" />
      </g>
      <g fill="#a78bfa">
        <rect x="520" y="240" width="9" height="52" /><rect x="536" y="258" width="9" height="34" />
      </g>
      <g fill="#3730a3">
        <rect x="416" y="330" width="168" height="8" /><rect x="416" y="348" width="168" height="8" />
        <rect x="416" y="366" width="120" height="8" />
      </g>

      {/* Baris 3 */}
      <rect x="0" y="400" width="200" height="200" fill="#4338ca" />
      <rect x="34" y="470" width="92" height="92" fill="#22d3ee" />
      <polygon points="126,470 126,562 34,562" fill="#0e7490" opacity="0.35" />

      <rect x="200" y="400" width="200" height="200" fill="#6d28d9" />
      <polygon
        points="300,418 314,470 358,442 330,486 382,500 330,514 358,558 314,530 300,582 286,530 242,558 270,514 218,500 270,486 242,442 286,470"
        fill="#facc15"
      />

      <rect x="400" y="400" width="200" height="200" fill="#312e81" />
      <g stroke="#a78bfa" strokeWidth="2.5" fill="none">
        <line x1="500" y1="512" x2="500" y2="430" />
        <line x1="500" y1="512" x2="440" y2="452" /><line x1="500" y1="512" x2="560" y2="452" />
        <line x1="500" y1="512" x2="418" y2="512" /><line x1="500" y1="512" x2="582" y2="512" />
        <line x1="500" y1="512" x2="458" y2="440" /><line x1="500" y1="512" x2="542" y2="440" />
        <line x1="500" y1="512" x2="428" y2="482" /><line x1="500" y1="512" x2="572" y2="482" />
      </g>
      <g fill="#a78bfa">
        <circle cx="500" cy="430" r="4" /><circle cx="440" cy="452" r="4" /><circle cx="560" cy="452" r="4" />
        <circle cx="418" cy="512" r="4" /><circle cx="582" cy="512" r="4" />
        <circle cx="458" cy="440" r="4" /><circle cx="542" cy="440" r="4" />
        <circle cx="428" cy="482" r="4" /><circle cx="572" cy="482" r="4" />
      </g>
      <g fill="#4f46e5">
        <circle cx="440" cy="568" r="6" /><circle cx="470" cy="568" r="6" />
        <circle cx="500" cy="568" r="6" /><circle cx="530" cy="568" r="6" />
      </g>

      {/* Baris 4 */}
      <rect x="0" y="600" width="200" height="200" fill="#4f46e5" />
      <g stroke="#312e81" strokeWidth="5" fill="none" strokeLinecap="round">
        <path d="M20,700 q20,-16 40,0 t40,0 t40,0 t40,0" />
        <path d="M20,722 q20,-16 40,0 t40,0 t40,0 t40,0" />
        <path d="M20,744 q20,-16 40,0 t40,0 t40,0 t40,0" />
      </g>
      <g fill="#a78bfa">
        <rect x="20" y="636" width="160" height="7" /><rect x="20" y="654" width="110" height="7" />
      </g>

      <rect x="200" y="600" width="200" height="200" fill="#1e1b4b" />
      <path d="M200,600 L400,600 L400,800 A200,200 0 0 1 200,600 Z" fill="#312e81" />
      <circle cx="352" cy="666" r="7" fill="#ffffff" />

      <rect x="400" y="600" width="200" height="200" fill="#22d3ee" />
      <g fill="#ffffff">
        <circle cx="440" cy="650" r="4" /><circle cx="468" cy="650" r="4" /><circle cx="496" cy="650" r="4" /><circle cx="524" cy="650" r="4" /><circle cx="552" cy="650" r="4" />
        <circle cx="440" cy="678" r="4" /><circle cx="468" cy="678" r="4" /><circle cx="496" cy="678" r="4" /><circle cx="524" cy="678" r="4" /><circle cx="552" cy="678" r="4" />
        <circle cx="440" cy="706" r="4" /><circle cx="468" cy="706" r="4" /><circle cx="496" cy="706" r="4" /><circle cx="524" cy="706" r="4" /><circle cx="552" cy="706" r="4" />
        <circle cx="440" cy="734" r="4" /><circle cx="468" cy="734" r="4" /><circle cx="496" cy="734" r="4" /><circle cx="524" cy="734" r="4" /><circle cx="552" cy="734" r="4" />
      </g>
    </svg>
  );
}

export default function Login() {
  const { user, login, completeLogin, setUser, booted } = useAuth();
  const navigate = useNavigate();
  // step: credentials | enroll2fa | verify2fa | changePassword
  const [step, setStep] = useState("credentials");
  const [nip, setNip] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [preauthToken, setPreauthToken] = useState(null);
  const [enrollData, setEnrollData] = useState(null); // { secret, otpauthUri, qrPngBase64 }
  const [code, setCode] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // User with a fully valid session and no pending obligation → dashboard.
  if (booted && user && !user.mustChangePassword) {
    return <Navigate to="/dashboard" replace />;
  }
  // Resumed session (e.g. refresh) that still owes a password change.
  const effectiveStep = booted && user && user.mustChangePassword ? "changePassword" : step;
  const fail = (err) => setError(formatApiError(err?.response?.data?.detail) || err.message);
  const handleCredentials = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await login(nip.replace(/\s/g, ""), password);
      setPreauthToken(res.preauthToken);
      setCurrentPassword(password); // reuse for forced change-password step
      if (res.totpEnrolled) {
        setStep("verify2fa");
      } else {
        const setup = await twoFaSetup(res.preauthToken);
        setEnrollData(setup);
        setStep("enroll2fa");
      }
      setCode("");
    } catch (err) {
      fail(err);
    } finally {
      setSubmitting(false);
    }
  };
  const afterAccess = (userData) => {
    completeLogin(userData);
    if (userData.mustChangePassword) {
      setStep("changePassword");
    } else {
      navigate("/dashboard", { replace: true });
    }
  };
  const handleCode = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const userData =
        effectiveStep === "enroll2fa"
          ? await twoFaActivate(preauthToken, code.trim())
          : await twoFaVerify(preauthToken, code.trim());
      afterAccess(userData);
    } catch (err) {
      fail(err);
    } finally {
      setSubmitting(false);
    }
  };
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Konfirmasi password tidak sama.");
      return;
    }
    setSubmitting(true);
    try {
      await changePasswordRequest(currentPassword, newPassword);
      setUser((prev) => (prev ? { ...prev, mustChangePassword: false } : prev));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      fail(err);
    } finally {
      setSubmitting(false);
    }
  };
  const checks = passwordChecks(newPassword);
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#e9eaf7] p-4 sm:p-6 lg:p-8">
      <div className="grid w-full max-w-6xl grid-cols-1 overflow-hidden rounded-3xl bg-white shadow-xl shadow-indigo-900/10 lg:min-h-[660px] lg:grid-cols-2">
        {/* ── Panel kiri: formulir ── */}
        <div className="flex flex-col justify-between px-7 py-10 sm:px-12 lg:px-14">
          <div className="mx-auto w-full max-w-sm flex-1 lg:flex lg:flex-col lg:justify-center">
            {/* Logo */}
            <div className="mb-9 flex items-center gap-3">
              <div
                className="flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ background: "linear-gradient(135deg, #5b3df5 0%, #7c3aed 100%)" }}
              >
                <ShieldCheck className="h-6 w-6 text-white" strokeWidth={2.2} />
              </div>
              <div className="leading-tight">
                <div className="font-display text-[15px] font-bold tracking-tight text-slate-900">
                  Dashboard Irbansus
                </div>
                <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-slate-400">
                  Inspektorat Kab. Tasikmalaya
                </div>
              </div>
            </div>

            {effectiveStep === "credentials" ? (
              <>
                <div className="mb-8">
                  <h2 className="font-display text-[30px] font-extrabold leading-tight tracking-tight text-slate-900">
                    Selamat datang kembali !
                  </h2>
                  <p className="mt-2 text-[13px] text-slate-500">
                    Masuk menggunakan NIP untuk mengakses data &amp; informasi pengawasan.
                  </p>
                </div>
                <form onSubmit={handleCredentials} className="space-y-5" data-testid="login-form">
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700" htmlFor="nip">
                      NIP <span className="text-[#5b3df5]">*</span>
                    </label>
                    <input
                      id="nip"
                      data-testid="login-nip-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="username"
                      required
                      value={nip}
                      onChange={(e) => setNip(e.target.value.replace(/\D/g, ""))}
                      placeholder="NIP 18 digit"
                      maxLength={18}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700" htmlFor="password">
                      Kata Sandi <span className="text-[#5b3df5]">*</span>
                    </label>
                    <div className="relative mt-1.5">
                      <input
                        id="password"
                        data-testid="login-password-input"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="block w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 pr-10 text-[13px] text-slate-900 placeholder:text-slate-400 transition-colors focus:border-[#5b3df5] focus:outline-none focus:ring-2 focus:ring-[#5b3df5]/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600"
                        data-testid="toggle-password-visibility"
                        aria-label={showPassword ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  {error ? (
                    <div
                      data-testid="login-error"
                      role="alert"
                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700"
                    >
                      {error}
                    </div>
                  ) : null}
                  <button
                    type="submit"
                    data-testid="login-submit-btn"
                    disabled={submitting}
                    className={buttonClass}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Memproses…</span>
                      </>
                    ) : (
                      <span>Masuk</span>
                    )}
                  </button>
                </form>
              </>
            ) : null}

            {effectiveStep === "enroll2fa" || effectiveStep === "verify2fa" ? (
              <>
                <div className="mb-7">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5b3df5]">
                    Verifikasi Dua Langkah
                  </div>
                  <h2 className="mt-2 font-display text-[26px] font-extrabold leading-tight tracking-tight text-slate-900">
                    {effectiveStep === "enroll2fa"
                      ? "Aktifkan Google Authenticator"
                      : "Masukkan Kode Autentikasi"}
                  </h2>
                  <p className="mt-2 text-[13px] text-slate-500">
                    {effectiveStep === "enroll2fa"
                      ? "Scan QR berikut dengan aplikasi Google Authenticator, lalu masukkan kode 6 digit yang muncul."
                      : "Buka aplikasi Google Authenticator di ponsel Anda dan masukkan kode 6 digit."}
                  </p>
                </div>
                {effectiveStep === "enroll2fa" && enrollData ? (
                  <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
                    <img
                      src={`data:image/png;base64,${enrollData.qrPngBase64}`}
                      alt="QR Google Authenticator"
                      data-testid="totp-qr"
                      className="mx-auto h-44 w-44 rounded-lg bg-white p-2 ring-1 ring-slate-200"
                    />
                    <div className="mt-3 text-[11px] text-slate-500">
                      Tidak bisa scan? Masukkan kode ini manual:
                    </div>
                    <code className="mt-1 inline-block select-all rounded bg-white px-2 py-1 text-[12px] font-semibold tracking-wider text-slate-800 ring-1 ring-slate-200">
                      {enrollData.secret}
                    </code>
                  </div>
                ) : null}
                <form onSubmit={handleCode} className="space-y-5" data-testid="totp-form">
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700" htmlFor="totp-code">
                      Kode 6 digit
                    </label>
                    <div className="relative mt-1.5">
                      <Smartphone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="totp-code"
                        data-testid="totp-code-input"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]*"
                        maxLength={6}
                        required
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="000000"
                        className="block w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 pl-10 text-center text-[18px] font-semibold tracking-[0.5em] text-slate-900 placeholder:text-slate-300 transition-colors focus:border-[#5b3df5] focus:outline-none focus:ring-2 focus:ring-[#5b3df5]/20"
                      />
                    </div>
                  </div>
                  {error ? (
                    <div
                      data-testid="totp-error"
                      role="alert"
                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700"
                    >
                      {error}
                    </div>
                  ) : null}
                  <button
                    type="submit"
                    data-testid="totp-submit-btn"
                    disabled={submitting || code.length !== 6}
                    className={buttonClass}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Memverifikasi…</span>
                      </>
                    ) : (
                      <span>{effectiveStep === "enroll2fa" ? "Aktifkan & Masuk" : "Verifikasi"}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("credentials");
                      setPreauthToken(null);
                      setEnrollData(null);
                      setCode("");
                      setError("");
                    }}
                    className="block w-full text-center text-[12px] font-medium text-slate-500 hover:text-slate-700"
                  >
                    ← Kembali ke halaman masuk
                  </button>
                </form>
              </>
            ) : null}

            {effectiveStep === "changePassword" ? (
              <>
                <div className="mb-7">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5b3df5]">
                    Keamanan Akun
                  </div>
                  <h2 className="mt-2 font-display text-[26px] font-extrabold leading-tight tracking-tight text-slate-900">
                    Ganti Password Anda
                  </h2>
                  <p className="mt-2 text-[13px] text-slate-500">
                    Demi keamanan, Anda wajib mengganti password sebelum melanjutkan.
                  </p>
                </div>
                <form onSubmit={handleChangePassword} className="space-y-5" data-testid="change-password-form">
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700" htmlFor="current-password">
                      Password Saat Ini
                    </label>
                    <input
                      id="current-password"
                      data-testid="current-password-input"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700" htmlFor="new-password">
                      Password Baru
                    </label>
                    <div className="relative mt-1.5">
                      <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="new-password"
                        data-testid="new-password-input"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimal 12 karakter"
                        className="block w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 pl-10 pr-10 text-[13px] text-slate-900 placeholder:text-slate-400 transition-colors focus:border-[#5b3df5] focus:outline-none focus:ring-2 focus:ring-[#5b3df5]/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600"
                        aria-label={showPassword ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <ul className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1">
                      {checks.map((c) => (
                        <li
                          key={c.label}
                          className={`text-[11px] ${c.ok ? "text-emerald-600" : "text-slate-400"}`}
                        >
                          {c.ok ? "✓" : "•"} {c.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700" htmlFor="confirm-password">
                      Ulangi Password Baru
                    </label>
                    <input
                      id="confirm-password"
                      data-testid="confirm-password-input"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputClass}
                    />
                  </div>
                  {error ? (
                    <div
                      data-testid="change-password-error"
                      role="alert"
                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700"
                    >
                      {error}
                    </div>
                  ) : null}
                  <button
                    type="submit"
                    data-testid="change-password-submit-btn"
                    disabled={submitting || !checks.every((c) => c.ok) || confirmPassword.length === 0}
                    className={buttonClass}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Menyimpan…</span>
                      </>
                    ) : (
                      <span>Simpan & Lanjutkan</span>
                    )}
                  </button>
                </form>
              </>
            ) : null}
          </div>

          {/* Footer */}
          <div className="mx-auto mt-10 flex w-full max-w-sm items-center justify-between text-[11px] text-slate-400">
            <span>© {new Date().getFullYear()} Pemkab Tasikmalaya</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-500">
              Versi 01
            </span>
          </div>
        </div>

        {/* ── Panel kanan: mozaik dekoratif ── */}
        <div className="relative hidden overflow-hidden bg-[#1e1b4b] lg:block">
          <MosaicPanel />
        </div>
      </div>
    </div>
  );
}
